import {
  useState, useEffect, useCallback,
} from 'react';

const FORM_NAV_TABS_ID = 'form-nav-tabs';
const FORM_STICKY_TABS_HEIGHT_VAR = '--form-sticky-tabs-height';
/** Matches --spacing-s in FormTabAnchors.module.css */
const FORM_STICKY_SPACING = 16;

/**
 * Syncs sticky tabs height to a CSS variable on the scroll container and exposes
 * scroll offset for react-scroll plus bottom padding so the last anchor can reach the top.
 * @param {string | undefined} containerId - Scroll container element id
 * @param {boolean} [enabled=true] - When false, skips DOM observation
 * @returns {{ scrollOffset: number, scrollPaddingBottom: number }}
 */
function useFormScrollContainer(containerId, enabled = true) {
  const [scrollOffset, setScrollOffset] = useState(0);
  const [scrollPaddingBottom, setScrollPaddingBottom] = useState(0);

  const sync = useCallback(() => {
    const container = containerId ? document.getElementById(containerId) : null;

    if (!enabled || !container) {
      setScrollOffset(0);
      setScrollPaddingBottom(0);
      return;
    }

    const navTabs = document.getElementById(FORM_NAV_TABS_ID);
    const tabsHeight = navTabs?.offsetHeight ?? 0;
    container.style.setProperty(FORM_STICKY_TABS_HEIGHT_VAR, `${tabsHeight}px`);

    const offset = tabsHeight + FORM_STICKY_SPACING;
    setScrollOffset(offset);
    setScrollPaddingBottom(Math.max(0, container.clientHeight - offset));
  }, [containerId, enabled]);

  useEffect(() => {
    if (!enabled || !containerId) {
      setScrollOffset(0);
      setScrollPaddingBottom(0);
      return undefined;
    }

    sync();

    const container = document.getElementById(containerId);
    const navTabs = document.getElementById(FORM_NAV_TABS_ID);
    let resizeObserver;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(sync);
      if (container) resizeObserver.observe(container);
      if (navTabs) resizeObserver.observe(navTabs);
    }

    window.addEventListener('resize', sync);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', sync);
      if (container) {
        container.style.removeProperty(FORM_STICKY_TABS_HEIGHT_VAR);
      }
    };
  }, [containerId, enabled, sync]);

  return { scrollOffset, scrollPaddingBottom };
}

export default useFormScrollContainer;
